/**
 * Gift Item Grouping
 * 
 * This script modifies the cart display to show gift options as part of their parent product
 * rather than as separate line items, while maintaining them as actual line items in the backend.
 */

class GiftItemGrouping {
    constructor() {
        this.selectors = {
            cartItem: '.m-cart-item',
            cartItemInfo: '.m-cart-item__info',
            giftOptionProperties: '[data-gift-option]',
            giftItems: '.m-cart-item[data-gift-item]',
            cartContainer: '.m-cart-items, .m-cart-drawer__items'
        };

        this.cartDrawer = document.querySelector('m-cart-drawer');
        this.mainCart = document.querySelector('m-cart');
        this.debug = false; // Enable debug mode
        this.processingTimeout = null;
        this.cartPageInterval = null;

        // Also track gift options that are stored as line item properties
        this.giftOptionProperties = [
            'Gift Option',
            '_gift_option_id',
            '_gift_option_price',
            '_gift_option_type',
            '_gift_type'
        ];

        // Store reference to the instance
        window.giftItemGroupingInstance = this;
        window.giftItemGroupingInitialized = true;

        // Set up page unload cleanup
        window.addEventListener('beforeunload', this.cleanup.bind(this));

        // Also listen for cart drawer open/close events
        document.addEventListener('cart:open', () => {
            this.log('Cart drawer opened (cart:open event)');
            setTimeout(() => this.processCart(), 300);
        });

        // Listen for cart updates
        document.addEventListener('cart:updated', () => {
            this.log('Cart updated event detected');
            setTimeout(() => this.processCart(), 300);
        });

        // Listen for all Shopify-specific cart events
        ['cart:updated', 'cart_update', 'update-cart', 'ajax:success', 'cart.requestComplete'].forEach(event => {
            document.addEventListener(event, () => {
                this.log(`Shopify cart event detected: ${event}`);
                setTimeout(() => this.processCart(), 300);
            });
        });

        // Also listen for checkout events
        this.setupCheckoutListeners();

        this.init();
    }

    log(message, data = null) {
        if (!this.debug) return;

        if (data) {
            console.log(`[GiftItemGrouping] ${message}`, data);
        } else {
            console.log(`[GiftItemGrouping] ${message}`);
        }
    }

    init() {
        // Process cart on page load after a delay to ensure DOM is fully ready
        this.log('Initializing gift item grouping');

        // Check if we're on the cart page
        const isCartPage = window.location.pathname.includes('/cart');
        if (isCartPage) {
            this.log('On cart page, using longer delay for initial processing');
            // Use a longer delay for cart page
            setTimeout(() => this.processCart(), 1000);
        } else {
            setTimeout(() => this.processCart(), 500);
        }

        // Re-process when cart is updated
        document.addEventListener('cart:updated', () => {
            this.log('Cart updated event detected');
            this.triggerCartProcessing();
        });

        // Register global method for other scripts to trigger processing
        window.processGiftItems = () => {
            this.log('Manual processGiftItems call detected');
            this.processCart();
        };

        // Listen for DOM updates that might indicate cart changes
        this.setupDomObserver();

        // If on the cart page, add a special handler for the main cart items
        if (isCartPage) {
            this.log('Setting up special cart page handlers');
            // Look for the main cart section and observe it
            const cartSection = document.querySelector('.main-cart, .cart, #CartDrawer, [data-section-id*="cart"]');
            if (cartSection) {
                this.log('Found main cart section, setting up mutation observer');
                const cartObserver = new MutationObserver((mutations) => {
                    this.log('Main cart section mutated, triggering processing');
                    this.triggerCartProcessing();
                });

                cartObserver.observe(cartSection, {
                    childList: true,
                    subtree: true,
                    attributes: true
                });
            }

            // Also set up a periodic refresh on the cart page
            this.cartPageInterval = setInterval(() => {
                this.log('Periodic cart page refresh');
                this.processCart();
            }, 3000); // Every 3 seconds
        }

        // Listen for all possible cart update events
        const possibleEvents = [
            'cart:refresh',
            'cart:updated',
            'cart_update',
            'update-cart',
            'cart.requestComplete',
            'cart:change',
            'ajax:success'
        ];

        possibleEvents.forEach(eventName => {
            document.addEventListener(eventName, () => {
                this.log(`Cart event detected: ${eventName}`);
                this.triggerCartProcessing();
            });
        });

        // If using cart drawer, also listen for its open event
        if (this.cartDrawer) {
            this.log('Cart drawer detected, listening for open events');
            if (window.MinimogEvents && MinimogTheme.pubSubEvents) {
                window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.openCartDrawer, () => {
                    this.log('Cart drawer opened via MinimogEvents');
                    this.triggerCartProcessing();
                });
            }

            // Listen for drawer opening by observing DOM
            this.observeCartDrawer();
        }

        // Also observe any cart containers for changes
        this.observeCartContainers();
    }

    // Setup DOM observer to watch for critical DOM changes
    setupDomObserver() {
        const domObserver = new MutationObserver((mutations) => {
            let shouldProcess = false;

            // Check if any mutations are relevant to the cart
            for (const mutation of mutations) {
                // Look for added nodes that might be cart items
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === 1) { // Element node
                            // Check if this is a cart item or contains cart items
                            if (
                                node.classList &&
                                (
                                    node.classList.contains('m-cart-item') ||
                                    node.querySelector('.m-cart-item')
                                )
                            ) {
                                shouldProcess = true;
                                break;
                            }

                            // Also look for gift-related elements
                            if (
                                node.textContent &&
                                (
                                    node.textContent.toLowerCase().includes('gift box') ||
                                    node.textContent.toLowerCase().includes('gift bag')
                                )
                            ) {
                                shouldProcess = true;
                                break;
                            }
                        }
                    }
                }

                if (shouldProcess) break;
            }

            if (shouldProcess) {
                this.log('Relevant DOM changes detected, triggering cart processing');
                this.triggerCartProcessing();
            }
        });

        // Observe the body for high-level changes
        domObserver.observe(document.body, {
            childList: true,
            subtree: true,
            characterData: false,
            attributes: false
        });

        this.log('DOM observer set up');
    }

    // Trigger cart processing with debounce to avoid multiple calls
    triggerCartProcessing() {
        if (this.processingTimeout) {
            clearTimeout(this.processingTimeout);
        }

        this.processingTimeout = setTimeout(() => {
            this.processCart();
        }, 300);
    }

    // Observe cart drawer for changes
    observeCartDrawer() {
        const drawerObserver = new MutationObserver((mutations) => {
            for (let mutation of mutations) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                    const drawer = mutation.target;
                    if (drawer.hasAttribute('open')) {
                        this.log('Cart drawer opened (detected via attributes)');
                        this.triggerCartProcessing();
                    }
                }
            }
        });

        // Look for drawer elements
        const possibleDrawers = document.querySelectorAll('aside[id*="cart"], dialog[id*="cart"], div[id*="cart-drawer"]');
        possibleDrawers.forEach(drawer => {
            this.log(`Observing cart drawer: ${drawer.id || drawer.className}`);
            drawerObserver.observe(drawer, { attributes: true });
        });
    }

    // Observe cart containers for changes
    observeCartContainers() {
        const containerObserver = new MutationObserver((mutations) => {
            this.log('Cart container mutation detected');
            this.triggerCartProcessing();
        });

        // Find cart containers and observe them
        const containers = document.querySelectorAll(this.selectors.cartContainer);
        containers.forEach(container => {
            this.log(`Observing cart container: ${container.className}`);
            containerObserver.observe(container, {
                childList: true,
                subtree: true
            });
        });
    }

    processCart() {
        // Check for recently selected gift options saved in localStorage
        try {
            const storedOption = localStorage.getItem('lastSelectedGiftOption');
            if (storedOption) {
                const giftOption = JSON.parse(storedOption);
                // Check if it's recent (within last 5 seconds)
                const ageInSeconds = (Date.now() - giftOption.timestamp) / 1000;

                if (ageInSeconds < 5) {
                    this.log(`Found recently selected gift option in localStorage: ${giftOption.title}`, 'success');

                    // Look for cart items without gift options that might need this one
                    setTimeout(() => {
                        const cartItems = document.querySelectorAll('.m-cart-item');
                        let appliedToItem = false;

                        cartItems.forEach(item => {
                            // Skip if this already has gift options
                            if (this.hasGiftOptionProperties(item)) return;

                            // Skip gift items themselves
                            const itemTitle = item.querySelector('.m-cart-item__title')?.textContent.trim() || '';
                            if (itemTitle.toLowerCase().includes('gift box') ||
                                itemTitle.toLowerCase().includes('gift bag')) return;

                            // Add gift checkbox and check it
                            const checkbox = this.ensureGiftCheckboxExists(item);
                            if (checkbox && !checkbox.checked) {
                                checkbox.checked = true;
                                this.log(`Pre-checked gift checkbox for item based on localStorage: ${itemTitle}`);

                                // Create cart item representation for adding the gift option
                                const regularItem = {
                                    element: item,
                                    productId: item.dataset.productId || '',
                                    handle: item.dataset.productHandle || '',
                                    title: itemTitle
                                };

                                // Get the cart item key
                                const itemKey = item.querySelector('[data-item-key]')?.dataset.itemKey;
                                if (itemKey) {
                                    // Add properties formatted from the stored option
                                    const formattedOption = {
                                        id: giftOption.variantId,
                                        title: giftOption.title,
                                        price: giftOption.price,
                                        type: giftOption.isGiftBox ? 'box' :
                                            giftOption.isGiftBag ? 'bag' : 'gift'
                                    };

                                    this.addGiftOptionProperty(itemKey, formattedOption, regularItem);
                                    appliedToItem = true;

                                    // Clear localStorage to prevent reapplication
                                    localStorage.removeItem('lastSelectedGiftOption');
                                }
                            }
                        });

                        if (!appliedToItem) {
                            this.log('Could not find a suitable cart item to apply stored gift option');
                        }
                    }, 300);
                } else {
                    // Clear old data
                    localStorage.removeItem('lastSelectedGiftOption');
                }
            }
        } catch (e) {
            this.log(`Error checking localStorage for gift options: ${e.message}`, 'error');
        }

        // Allow DOM to update first
        setTimeout(() => {
            // Get all cart items
            const cartItems = document.querySelectorAll(this.selectors.cartItem);
            this.log(`Processing cart with ${cartItems.length} items`);
            if (!cartItems.length) return;

            // First pass: identify gift items and their properties
            const giftItems = [];
            const regularItems = [];
            const productsWithIncludedGiftBoxes = new Set(); // Track products with gift boxes already
            const itemsWithGiftProperties = new Set(); // Track items with gift properties

            // First identify products with included gift boxes and those with gift options
            cartItems.forEach(item => {
                const itemContent = item.textContent || '';
                const productId = item.dataset.productId || '';
                if (!productId) return;

                // Search for all possible ways a gift box inclusion might be indicated
                const hasPremiumGiftBox =
                    // Text content checks
                    itemContent.includes('Premium Gift Box Included') ||
                    itemContent.includes('Gift Box Included') ||

                    // Direct metafield checks
                    item.querySelector('[data-gift-box-included="true"]') !== null ||
                    item.querySelector('.gift-box-included') !== null ||

                    // Hidden input or data-* attributes 
                    item.querySelector('input[name*="gift_box_included"][value="true"]') !== null ||
                    item.hasAttribute('data-gift-box-included') ||

                    // Special elements or classes
                    item.querySelector('.m-cart-item__gift-box-included') !== null ||
                    item.querySelector('.premium-gift-box') !== null ||

                    // Look for any specific HTML structure that indicates gift box inclusion
                    item.querySelector('.gift-box-included-text') !== null ||

                    // Class-based check
                    item.classList.contains('has-gift-box-included');

                if (hasPremiumGiftBox) {
                    this.log(`Product ${productId} has a premium gift box included`);
                    productsWithIncludedGiftBoxes.add(productId);

                    // Remove any gift checkboxes that might have been added
                    const checkbox = item.querySelector('.gift-checkbox-container');
                    if (checkbox) {
                        this.log('Removing gift checkbox from product with included gift box');
                        checkbox.remove();
                    }
                }

                // Check for gift option properties on this item
                const hasGiftOptionProperty = this.hasGiftOptionProperties(item);
                if (hasGiftOptionProperty) {
                    this.log(`Item has gift option properties: ${item.querySelector('.m-cart-item__title')?.textContent.trim()}`);
                    itemsWithGiftProperties.add(item.dataset.itemKey || '');

                    // Ensure the checkbox is checked and property displayed
                    const giftCheckbox = this.ensureGiftCheckboxExists(item);
                    if (giftCheckbox) {
                        giftCheckbox.checked = true;
                        this.log('Ensured gift checkbox is checked for item with gift properties');
                    }

                    // Create the visual representation
                    this.createGiftOptionFromProperties(item);
                }
            });

            // Now process all cart items
            cartItems.forEach(item => {
                const itemKey = item.querySelector('[data-item-key]')?.dataset.itemKey;
                const itemTitle = item.querySelector('.m-cart-item__title')?.textContent.trim() || '';
                const productHandle = item.dataset.productHandle || '';
                const productId = item.dataset.productId || '';

                // Enhanced gift item detection - more robust checking
                const isGiftItem = (
                    // Check title for gift-related terms
                    itemTitle.toLowerCase().includes('gift box') ||
                    itemTitle.toLowerCase().includes('gift bag') ||
                    itemTitle.toLowerCase().includes('gift wrapping') ||
                    itemTitle.toLowerCase().includes('la jolla') && (
                        itemTitle.toLowerCase().includes('box') ||
                        itemTitle.toLowerCase().includes('bag')
                    ) ||
                    // Check product handle
                    productHandle === 'gift-wrapping' ||
                    productHandle === 'gift-box' ||
                    productHandle === 'gift-bag' ||
                    // Check for data attributes or properties that indicate this is a gift item
                    item.querySelector('[data-gift-option]') ||
                    item.hasAttribute('data-gift-item')
                ) &&
                    // But make sure it's not a normal product that just has "gift" in the title
                    !itemTitle.includes('Premium Gift Box Included');

                if (isGiftItem) {
                    this.log(`Found gift item: "${itemTitle}" with key ${itemKey}`);

                    // Get properties including the parent product ID if available
                    const properties = {};
                    const propertyElements = item.querySelectorAll('.m-cart-item__property');

                    propertyElements.forEach(prop => {
                        const key = prop.querySelector('[data-cart-item-property-name]')?.textContent.replace(':', '').trim();
                        const value = prop.querySelector('[data-cart-item-property-value]')?.textContent.trim();

                        if (key && value) {
                            properties[key] = value;
                        }
                    });

                    this.log(`Gift item properties:`, properties);

                    // Check for parent product ID from properties
                    const parentProductId = properties['_product_id'] || null;

                    // Expanded check: Also look at HTML data attributes that might contain relationship info
                    const dataParentId = item.dataset.parentProductId || '';
                    const finalParentId = parentProductId || dataParentId;

                    // Check if this gift item has a parent product in the cart
                    const hasParentProduct =
                        finalParentId &&
                        Array.from(cartItems).some(item => item.dataset.productId === finalParentId);

                    if (!hasParentProduct) {
                        this.log(`Gift item ${itemKey} has no parent product or parent was removed, showing as normal line item`);
                        // Skip this gift item - leave it as a normal line item
                        item.style.display = ''; // Make sure it's visible
                        item.removeAttribute('data-gift-item'); // Remove gift item flag
                    } else {
                        // Only process gift items with parent products
                        // Store gift item data
                        giftItems.push({
                            element: item,
                            itemKey: itemKey,
                            properties: properties,
                            parentId: finalParentId,
                            parentHandle: properties['_product_handle'] || null,
                            productId: productId,
                            handle: productHandle,
                            image: item.querySelector('.m-cart-item__image img')?.src || '',
                            title: itemTitle,
                            price: item.querySelector('.m-cart-item__price--regular')?.textContent.trim() || '',
                            variant: item.querySelector('.m-cart-item__option')?.textContent.trim() || ''
                        });

                        // Hide the original gift item
                        item.style.display = 'none';
                        item.setAttribute('data-gift-item', 'true');
                    }
                } else if (productId && !productsWithIncludedGiftBoxes.has(productId)) {
                    // Only add to regular items if it's not a gift item
                    this.log(`Found regular item: "${itemTitle}"`);
                    regularItems.push({
                        element: item,
                        itemKey: itemKey,
                        productId: productId,
                        handle: productHandle,
                        title: itemTitle
                    });
                }
            });

            this.log(`Identified ${giftItems.length} gift items and ${regularItems.length} regular items`);

            // Skip adding gift checkboxes if the cart is empty or all products have included gift boxes
            if (regularItems.length === 0) {
                this.log('No regular items found, or all products have included gift boxes, skipping gift checkboxes');
                return;
            }

            // Add "Make it a gift" checkboxes to regular items that don't have gift items
            regularItems.forEach(regularItem => {
                // Skip if this product already has a gift box included
                if (productsWithIncludedGiftBoxes.has(regularItem.productId)) {
                    this.log(`Skipping gift checkbox for product ${regularItem.productId} with included gift box`);
                    return;
                }

                // Check if this item has gift option properties
                const hasGiftProperties = this.hasGiftOptionProperties(regularItem.element);

                // Look for gift checkbox - add if it doesn't exist
                const existingCheckbox = regularItem.element.querySelector('.make-it-gift-checkbox');
                if (!existingCheckbox) {
                    this.addGiftCheckbox(regularItem);

                    // If this item has gift properties, check the box
                    if (hasGiftProperties) {
                        const checkbox = regularItem.element.querySelector('.make-it-gift-checkbox');
                        if (checkbox) {
                            checkbox.checked = true;
                            this.log(`Pre-checked gift checkbox for item with gift properties`);
                        }
                    }
                } else if (hasGiftProperties && !existingCheckbox.checked) {
                    // If checkbox exists but isn't checked, check it
                    existingCheckbox.checked = true;
                    this.log(`Updated existing checkbox to checked state for item with gift properties`);
                }

                // If this item has gift properties, create the visual representation
                if (hasGiftProperties) {
                    this.createGiftOptionFromProperties(regularItem.element);
                }
            });

            // Second pass: attach gift items to their parent products with enhanced matching
            regularItems.forEach(regularItem => {
                // Skip products with included gift boxes
                if (productsWithIncludedGiftBoxes.has(regularItem.productId)) {
                    return;
                }

                // Enhanced matching: try multiple ways to match gift items to their parents
                const matchingGiftItems = giftItems.filter(giftItem => {
                    // Direct match by parentId from properties
                    if (giftItem.parentId && giftItem.parentId === regularItem.productId) {
                        this.log(`Match by parentId: ${giftItem.title} -> ${regularItem.title}`);
                        return true;
                    }

                    // Match by properties._product_id
                    if (giftItem.properties &&
                        regularItem.productId &&
                        giftItem.properties['_product_id'] === regularItem.productId) {
                        this.log(`Match by _product_id property: ${giftItem.title} -> ${regularItem.title}`);
                        return true;
                    }

                    // Match by title relationships (e.g., when properties aren't available)
                    // This helps with items that were already in the cart
                    const giftTitle = giftItem.title.toLowerCase();
                    const productTitle = regularItem.title.toLowerCase();

                    // Look for product-specific markers in gift item properties
                    if (giftItem.properties && regularItem.handle) {
                        if (giftItem.properties['_product_handle'] === regularItem.handle) {
                            this.log(`Match by product handle in properties: ${giftItem.title} -> ${regularItem.title}`);
                            return true;
                        }
                    }

                    // If we're dealing with gift items without properties, try proximity-based matching
                    // Items added together are often related
                    const giftIndex = Array.from(cartItems).indexOf(giftItem.element);
                    const productIndex = Array.from(cartItems).indexOf(regularItem.element);

                    // If the gift item is right after or before the product (common pattern), associate them
                    if (Math.abs(giftIndex - productIndex) === 1) {
                        this.log(`Match by proximity: ${giftItem.title} -> ${regularItem.title}`);
                        return true;
                    }

                    return false;
                });

                if (matchingGiftItems.length) {
                    this.log(`Attaching ${matchingGiftItems.length} gift items to "${regularItem.title}"`);
                    this.attachGiftItemsToProduct(regularItem.element, matchingGiftItems, productsWithIncludedGiftBoxes.has(regularItem.productId));

                    // Update the checkbox state if there are gift items
                    const checkbox = regularItem.element.querySelector('input.make-it-gift-checkbox');
                    if (checkbox) {
                        checkbox.checked = true;
                    }
                }
            });

            // Find unprocessed gift items and show them as normal line items
            cartItems.forEach(item => {
                if (item.hasAttribute('data-gift-item') && !item.hasAttribute('data-processed-gift-item')) {
                    this.log('Found unprocessed gift item, showing as normal line item');
                    item.style.display = '';  // Show it
                    item.removeAttribute('data-gift-item');  // Remove the gift item flag
                }
            });

            // Add event listeners for newly created elements
            this.addEventListeners();
        }, 200);
    }

    attachGiftItemsToProduct(productElement, giftItems, hasIncludedGiftBox = false) {
        const productTitle = productElement.querySelector('.m-cart-item__title')?.textContent.trim() || 'Unknown Product';
        this.log(`Attaching gift items to product "${productTitle}", hasIncludedGiftBox=${hasIncludedGiftBox}`);

        // Store product info for later reference
        const productId = productElement.dataset.productId || '';
        const productHandle = productElement.dataset.productHandle || '';

        // Create container for gift options if it doesn't exist
        let giftOptionsContainer = productElement.querySelector('.product-gift-options');

        if (!giftOptionsContainer) {
            this.log('Creating new gift options container');
            giftOptionsContainer = document.createElement('div');
            giftOptionsContainer.className = 'product-gift-options';

            // Store product ID in the container for future reference
            giftOptionsContainer.dataset.parentProductId = productId;
            giftOptionsContainer.dataset.parentProductHandle = productHandle;
            giftOptionsContainer.dataset.parentProductTitle = productTitle;

            // Insert after the product info
            const productInfo = productElement.querySelector(this.selectors.cartItemInfo);
            if (productInfo) {
                productInfo.appendChild(giftOptionsContainer);
                this.log('Added gift options container to product info');
            } else {
                this.log('Could not find product info element to attach gift options container');
            }
        } else {
            this.log('Found existing gift options container');

            // Update product info in the container
            giftOptionsContainer.dataset.parentProductId = productId;
            giftOptionsContainer.dataset.parentProductHandle = productHandle;
            giftOptionsContainer.dataset.parentProductTitle = productTitle;
        }

        // Clear existing content
        giftOptionsContainer.innerHTML = '';

        // Add each gift item
        giftItems.forEach(giftItem => {
            this.log(`Creating visual element for gift item "${giftItem.title}"`);
            // Mark gift item as processed to prevent duplicate processing
            giftItem.element.setAttribute('data-processed-gift-item', 'true');

            const giftOptionEl = document.createElement('div');
            giftOptionEl.className = 'product-gift-option';
            giftOptionEl.dataset.giftItemKey = giftItem.itemKey;

            // Store parent product info directly on the gift option element
            giftOptionEl.dataset.parentProductId = productId;
            giftOptionEl.dataset.parentProductHandle = productHandle;
            giftOptionEl.dataset.parentProductTitle = productTitle;

            // Determine the gift type more accurately
            let giftType = 'Gift Option';
            let iconClass = '';

            if (giftItem.title.toLowerCase().includes('box')) {
                giftType = giftItem.title;
                iconClass = 'gift-box-icon';
            } else if (giftItem.title.toLowerCase().includes('bag')) {
                giftType = giftItem.title;
                iconClass = 'gift-bag-icon';
            } else if (giftItem.title.toLowerCase().includes('wrap')) {
                giftType = giftItem.title;
                iconClass = 'gift-wrap-icon';
            }

            // Create the HTML with or without remove button based on whether gift box is included
            const removeButton = !hasIncludedGiftBox ?
                `<button class="gift-option-remove" data-item-key="${giftItem.itemKey}">Remove</button>` :
                '';

            // Make sure we display both title and price properly
            giftOptionEl.innerHTML = `
        <div class="gift-option-content">
          ${giftItem.image ? `<img src="${giftItem.image}" alt="${giftType}" class="gift-option-image ${iconClass}">` : ''}
          <div class="gift-option-info">
            <span class="gift-option-variant">${giftItem.title || giftType}</span>
            <span class="gift-option-price">${giftItem.price}</span>
          </div>
        </div>
        <div class="gift-option-actions">
          ${removeButton}
        </div>
      `;

            giftOptionsContainer.appendChild(giftOptionEl);
            this.log(`Added gift option element for "${giftType}" to container`);
        });
    }

    addEventListeners() {
        this.log('Adding event listeners to gift option elements');

        // Remove gift option
        document.querySelectorAll('.gift-option-remove').forEach(button => {
            if (!button.hasAttribute('data-initialized')) {
                button.setAttribute('data-initialized', 'true');
                button.addEventListener('click', this.handleRemoveGiftOption.bind(this));
                this.log(`Added remove listener to button with key ${button.dataset.itemKey}`);
            }
        });

        // Property-based remove buttons
        document.querySelectorAll('.gift-option-remove-property').forEach(button => {
            if (!button.hasAttribute('data-initialized')) {
                button.setAttribute('data-initialized', 'true');

                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const itemElement = button.closest('.m-cart-item');
                    if (!itemElement) {
                        this.log('Cannot find cart item for property removal', 'error');
                        return;
                    }

                    const itemKey = itemElement.querySelector('[data-item-key]')?.dataset.itemKey;
                    if (!itemKey) {
                        this.log('Cannot find item key for property removal', 'error');
                        return;
                    }

                    this.removeGiftOptionProperty(itemKey, itemElement);
                });

                this.log(`Added property remove listener to button`);
            }
        });

        // Add listeners for gift checkboxes
        document.querySelectorAll('.make-it-gift-checkbox').forEach(checkbox => {
            if (!checkbox.hasAttribute('data-initialized')) {
                checkbox.setAttribute('data-initialized', 'true');

                // Find the regular item this checkbox belongs to
                const cartItem = checkbox.closest('.m-cart-item');
                if (cartItem) {
                    const productId = cartItem.dataset.productId;
                    const productHandle = cartItem.dataset.productHandle;
                    const productTitle = cartItem.querySelector('.m-cart-item__title')?.textContent.trim() || '';

                    const regularItem = {
                        element: cartItem,
                        productId: productId,
                        handle: productHandle,
                        title: productTitle
                    };

                    checkbox.addEventListener('change', (e) => this.handleGiftCheckboxChange(e, regularItem));
                    this.log(`Added gift checkbox listener for product ${productTitle}`);
                }
            }
        });
    }

    handleRemoveGiftOption(event) {
        const button = event.currentTarget;
        const itemKey = button.dataset.itemKey;

        if (!itemKey) {
            this.log('Remove button clicked but no item key found');
            return;
        }

        this.log(`Removing gift option with key: ${itemKey}`);

        // Prevent the default action and stop propagation
        event.preventDefault();
        event.stopPropagation();

        // Show loading state
        button.textContent = 'Removing...';
        button.disabled = true;

        // Update cart quantity to 0 for this item
        fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: itemKey,
                quantity: 0
            })
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to remove item: ${response.status}`);
                }
                return response.json();
            })
            .then(cart => {
                this.log(`Gift option removed successfully, new cart count: ${cart.item_count}`);

                // Find and remove the visual element
                const giftOption = button.closest('.product-gift-option');
                if (giftOption) {
                    giftOption.remove();
                }

                // If on cart page, reload the page to ensure everything is updated correctly
                if (window.location.pathname.includes('/cart')) {
                    window.location.href = window.location.pathname + '?updated=' + Date.now();
                    return;
                }

                // For cart drawer, update normally
                if (this.cartDrawer && typeof this.cartDrawer.onCartDrawerUpdate === 'function') {
                    this.cartDrawer.onCartDrawerUpdate();

                    // Re-process after cart drawer update
                    setTimeout(() => {
                        this.processCart();
                    }, 500);
                } else if (this.mainCart && typeof this.mainCart.onCartUpdate === 'function') {
                    this.mainCart.onCartUpdate();

                    // Re-process after cart update
                    setTimeout(() => {
                        this.processCart();
                    }, 500);
                } else {
                    // Force reload the page if we can't update the cart UI
                    setTimeout(() => {
                        window.location.href = window.location.pathname + '?cart=updated';
                    }, 500);
                }
            })
            .catch(error => {
                this.log(`Error removing gift option: ${error.message}`, 'error');
                button.textContent = 'Remove';
                button.disabled = false;

                // If on cart page and there was an error, still try to reload
                if (window.location.pathname.includes('/cart')) {
                    window.location.href = window.location.pathname + '?error=true';
                    return;
                }

                // Show error notification
                if (window.MinimogTheme && window.MinimogTheme.Notification) {
                    window.MinimogTheme.Notification.show({
                        target: button.closest('.product-gift-option'),
                        type: 'error',
                        message: 'Could not remove item. Please try again.',
                    });
                }
            });
    }

    // Cleanup function to clear any intervals or timeouts
    cleanup() {
        this.log('Cleaning up');
        if (this.processingTimeout) {
            clearTimeout(this.processingTimeout);
        }
        if (this.cartPageInterval) {
            clearInterval(this.cartPageInterval);
        }

        // Make sure we clean up drawer event handlers too
        this.cleanupDrawerEventHandlers();

        // Remove any CSS modifications
        const cartDrawerElem = document.querySelector('.m-cart-drawer, #cart-drawer, [data-section-type="cart-drawer"]');
        if (cartDrawerElem) {
            cartDrawerElem.classList.remove('prevent-close');
            cartDrawerElem.removeAttribute('data-updating');
        }
    }

    // Properly clean up all drawer event handlers
    cleanupDrawerEventHandlers() {
        if (this.drawerEventHandlers && this.drawerEventHandlers.length) {
            this.log(`Cleaning up ${this.drawerEventHandlers.length} drawer event handlers`);

            this.drawerEventHandlers.forEach(({ event, handler, capture }) => {
                document.removeEventListener(event, handler, capture);
            });

            // Clear the handlers array
            this.drawerEventHandlers = [];
        }
    }

    // Add "Make it a gift" checkbox to regular items
    addGiftCheckbox(regularItem) {
        // Find title area to add checkbox after
        const titleElement = regularItem.element.querySelector('.m-cart-item__title');
        if (!titleElement) return;

        // Check if this product already has a gift box included
        // Look for premium gift box text or properties in the cart item
        const itemContent = regularItem.element.textContent || '';

        // Helper function to find elements containing text
        const elementContainsText = (selector, text) => {
            const elements = regularItem.element.querySelectorAll(selector);
            return Array.from(elements).some(el => (el.textContent || '').includes(text));
        };

        // Check all possible indicators of an included gift box
        const hasIncludedGiftBox =
            // Text content checks
            itemContent.includes('Premium Gift Box Included') ||
            itemContent.includes('Gift Box Included') ||

            // Property list checks
            regularItem.element.querySelector('.m-cart-item__property-list [data-cart-item-property-value*="Gift Box"]') !== null ||

            // Description element checks with proper JS
            elementContainsText('.m-cart-item__description', 'Premium Gift Box') ||
            elementContainsText('.m-cart-item__property', 'Gift Box') ||

            // Check special Premium Gift Box element
            !!regularItem.element.querySelector('.m-cart-item__premium-gift-box');

        // Skip adding checkbox if gift box is already included
        if (hasIncludedGiftBox) {
            this.log(`Product "${regularItem.title}" already has gift box included, skipping checkbox`);
            return;
        }

        // Create checkbox container
        const checkboxContainer = document.createElement('div');
        checkboxContainer.className = 'gift-checkbox-container';
        checkboxContainer.style.marginTop = '6px';
        checkboxContainer.style.marginBottom = '6px';

        // Create the checkbox input
        checkboxContainer.innerHTML = `
            <label style="display: flex; align-items: center; font-size: 0.9em; color: #555;">
                <input type="checkbox" class="make-it-gift-checkbox" data-product-id="${regularItem.productId}" style="margin-right: 5px;">
                Make it a gift?
            </label>
        `;

        // Insert after title
        titleElement.after(checkboxContainer);

        // Add event listener to checkbox
        const checkbox = checkboxContainer.querySelector('input.make-it-gift-checkbox');
        if (checkbox) {
            // Set initial state - check if there are any gift options associated
            const hasGiftOption = !!regularItem.element.querySelector('.product-gift-options');
            checkbox.checked = hasGiftOption;

            // Add change event listener
            checkbox.addEventListener('change', (e) => this.handleGiftCheckboxChange(e, regularItem));
        }
    }

    // Handle "Make it a gift" checkbox change
    handleGiftCheckboxChange(event, regularItem) {
        const checkbox = event.target;
        const isChecked = checkbox.checked;
        const productId = regularItem.productId;
        const productTitle = regularItem.title;

        this.log(`Gift checkbox changed for product "${productTitle}" (${productId}), checked: ${isChecked}`);

        if (isChecked) {
            // First check if there are already gift options for this product
            const existingGiftOptions = regularItem.element.querySelectorAll('.product-gift-option');
            if (existingGiftOptions.length > 0) {
                this.log('This product already has gift options, not adding more');
                return;
            }

            // Also check if it already has gift option properties
            if (this.hasGiftOptionProperties(regularItem.element)) {
                this.log('This product already has gift option properties, not adding more');
                return;
            }

            // Show gift options dropdown
            this.showGiftOptionsDropdown(checkbox, regularItem);
        } else {
            // Check if the item has gift option properties
            if (this.hasGiftOptionProperties(regularItem.element)) {
                // Get the cart item key
                const itemKey = regularItem.element.querySelector('[data-item-key]')?.dataset.itemKey;
                if (itemKey) {
                    this.removeGiftOptionProperty(itemKey, regularItem.element);
                }
            } else {
                // Handle existing line-item based gift options
                const giftOptions = regularItem.element.querySelectorAll('.product-gift-option');
                if (giftOptions.length) {
                    giftOptions.forEach(giftOption => {
                        const itemKey = giftOption.dataset.giftItemKey;
                        if (itemKey) {
                            this.removeGiftOption(itemKey, regularItem);
                        } else {
                            giftOption.remove();
                        }
                    });
                }
            }
        }
    }

    // Show gift options dropdown
    showGiftOptionsDropdown(checkbox, regularItem) {
        // Remove any existing dropdowns
        document.querySelectorAll('.gift-options-simple-dropdown').forEach(el => el.remove());

        // Create dropdown element
        const dropdown = document.createElement('div');
        dropdown.className = 'gift-options-simple-dropdown';
        dropdown.style.marginTop = '5px';
        dropdown.style.marginBottom = '5px';
        dropdown.style.padding = '10px';
        dropdown.style.border = '1px solid #ddd';
        dropdown.style.borderRadius = '4px';
        dropdown.style.background = '#f9f9f9';
        dropdown.style.position = 'relative';
        dropdown.style.zIndex = '5';

        // Add options with images - with correct Euro prices
        const options = [
            {
                id: '50524897870165',
                title: '"La Jolla" Bright Film Gift Box',
                price: '€4,00',
                type: 'box',
                image: 'https://cdn.shopify.com/s/files/1/0928/3200/6485/files/La_Jolla_Box_AI.png?v=1743773369'
            },
            {
                id: '50524897902933',
                title: '"La Jolla" Bright Film Gift Bag',
                price: '€3,00',
                type: 'bag',
                image: 'https://cdn.shopify.com/s/files/1/0928/3200/6485/files/LaJollaBagAIOption1_2_2b67e194-ffa1-4f95-be44-28569abbac22.png?v=1743773396'
            }
        ];

        // Format prices using Shopify's currency converter if available
        if (window.Shopify && Shopify.currency && typeof Shopify.currency.convertAll === 'function') {
            // Convert all prices from EUR to current currency
            options.forEach(option => {
                // Parse price to get numeric value (remove € and convert , to .)
                const priceValue = parseFloat(option.price.replace('€', '').replace(',', '.'));
                // Format according to current currency
                option.price = this.formatMoney(priceValue * 100); // multiply by 100 to convert to cents
            });
        }

        dropdown.innerHTML = `
            <div style="margin-bottom: 12px; font-size: 0.95em; font-weight: 500; color: #333;">Select gift packaging:</div>
            <div style="display: flex; gap: 10px;">
                ${options.map((option, index) => `
                    <div class="gift-option-choice-with-image" data-option-id="${option.id}" style="flex: 1; cursor: pointer; border: 2px solid #eee; border-radius: 8px; padding: 8px; background: white; transition: all 0.2s ease;">
                        <div style="text-align: center; margin-bottom: 5px;">
                            <img src="${option.image}" alt="${option.title}" style="width: 100%; height: 80px; object-fit: contain; margin-bottom: 5px;">
                            <div style="font-size: 0.85em; font-weight: 500;">${option.title}</div>
                            <div style="font-size: 0.85em; font-weight: 600; color: #555;">${option.price}</div>
                        </div>
                    </div>
                `).join('')}
            </div>
            <div style="margin-top: 12px; display: flex; justify-content: space-between; align-items: center;">
                <button class="cancel-gift-option" style="padding: 6px 12px; background: #f5f5f5; color: #555; border: 1px solid #ddd; border-radius: 4px; cursor: pointer; font-size: 0.85em; font-weight: 500;">Cancel</button>
                <button class="apply-gift-option" style="padding: 6px 12px; background: #aebaa1; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 0.85em; font-weight: 500;">Apply</button>
            </div>
        `;

        // Add the dropdown after the checkbox
        const checkboxContainer = checkbox.closest('.gift-checkbox-container');
        checkboxContainer.appendChild(dropdown);

        // Add event listeners for option selection
        let selectedOption = null;
        dropdown.querySelectorAll('.gift-option-choice-with-image').forEach((choice, index) => {
            // Set the first option as selected initially
            if (index === 0) {
                choice.style.borderColor = '#aebaa1';
                choice.style.backgroundColor = 'rgba(174, 186, 161, 0.05)';
                selectedOption = choice.dataset.optionId;
            }

            // Add click handler
            choice.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Clear previous selection
                dropdown.querySelectorAll('.gift-option-choice-with-image').forEach(c => {
                    c.style.borderColor = '#eee';
                    c.style.backgroundColor = 'white';
                });

                // Mark this as selected
                choice.style.borderColor = '#aebaa1';
                choice.style.backgroundColor = 'rgba(174, 186, 161, 0.05)';
                selectedOption = choice.dataset.optionId;
            });
        });

        // Add cancel button handler
        const cancelButton = dropdown.querySelector('.cancel-gift-option');
        if (cancelButton) {
            cancelButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Uncheck the checkbox
                checkbox.checked = false;

                // Remove the dropdown
                dropdown.remove();
            });
        }

        // Add event listener for apply button with extra preventions
        const applyButton = dropdown.querySelector('.apply-gift-option');
        if (applyButton) {
            applyButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Prevent cart drawer from closing
                this.preventCartDrawerClose();

                if (selectedOption) {
                    // Show updating state
                    applyButton.textContent = 'Updating...';
                    applyButton.disabled = true;

                    // Remove dropdown after a short delay to show the user something is happening
                    setTimeout(() => {
                        dropdown.remove();

                        // Get the selected option details
                        const selectedOptionDetails = options.find(option => option.id === selectedOption);

                        // Get the cart item key
                        const itemKey = regularItem.element.querySelector('[data-item-key]')?.dataset.itemKey;
                        if (itemKey && selectedOptionDetails) {
                            // Add the gift option as a property to the existing item
                            this.addGiftOptionProperty(itemKey, selectedOptionDetails, regularItem);
                        } else {
                            // Fallback to the old method if we can't find the item key
                            this.addGiftOptionToProduct(regularItem, selectedOption);
                        }
                    }, 100);
                }
            });
        }
    }

    // Helper method to prevent cart drawer from closing
    preventCartDrawerClose() {
        if (this.cartDrawer) {
            try {
                // Try multiple approaches to keep the drawer open

                // Store any event handlers we add so we can remove them later
                this.drawerEventHandlers = this.drawerEventHandlers || [];

                // 1. Try to find and manipulate the drawer directly
                const cartDrawerElem = document.querySelector('.m-cart-drawer, #cart-drawer, [data-section-type="cart-drawer"]');
                if (cartDrawerElem) {
                    // Add class to prevent CSS transitions
                    cartDrawerElem.classList.add('prevent-close');
                    // For event-based closing prevention
                    cartDrawerElem.setAttribute('data-updating', 'true');

                    // If it's a dialog element, make sure it stays open
                    if (cartDrawerElem.tagName === 'DIALOG') {
                        // Ensure dialog remains open
                        if (!cartDrawerElem.hasAttribute('open')) {
                            cartDrawerElem.setAttribute('open', '');
                        }
                    }
                }

                // 2. Try to intercept any events that might close the drawer
                const stopCloseEvent = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                };

                // Add temporary listeners to prevent close events
                document.addEventListener('drawer:close', stopCloseEvent, true);
                this.drawerEventHandlers.push({ event: 'drawer:close', handler: stopCloseEvent, capture: true });

                // Remove prevention after a short delay
                setTimeout(() => {
                    this.cleanupDrawerEventHandlers();

                    // Also remove CSS classes and attributes
                    if (cartDrawerElem) {
                        cartDrawerElem.classList.remove('prevent-close');
                        cartDrawerElem.removeAttribute('data-updating');
                    }
                }, 2000);

            } catch (error) {
                this.log('Error preventing drawer close: ' + error);
            }
        }
    }

    // Add a gift option to a product
    addGiftOptionToProduct(regularItem, giftOptionId) {
        const productId = regularItem.productId;
        const productHandle = regularItem.handle;
        const productTitle = regularItem.title;

        this.log(`Adding gift option ${giftOptionId} to product "${productTitle}" (${productId})`);

        // Find the selected option details
        const options = [
            {
                id: '50524897870165',
                title: '"La Jolla" Bright Film Gift Box',
                price: '€4,00',
                type: 'box'
            },
            {
                id: '50524897902933',
                title: '"La Jolla" Bright Film Gift Bag',
                price: '€3,00',
                type: 'bag'
            }
        ];

        const selectedOption = options.find(option => option.id === giftOptionId) || options[0];
        const giftOptionTitle = selectedOption.title;
        const giftType = selectedOption.type === 'box' ? 'Box' : 'Bag';

        // Create properties for the gift option with clear relationship markers and the actual product name
        const properties = {
            'Gift Option': giftOptionTitle,
            '_gift_type': giftType,
            '_product_id': productId,
            '_product_handle': productHandle,
            '_product_title': productTitle,
            '_added_at': new Date().getTime()
        };

        // Add to cart
        fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                items: [
                    {
                        id: giftOptionId,
                        quantity: 1,
                        properties: properties
                    }
                ]
            })
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to add gift option: ${response.status}`);
                }
                return response.json();
            })
            .then(addResponse => {
                this.log(`Gift option added successfully, refreshing cart`);

                // Small delay before refreshing the cart to ensure Shopify's backend has updated
                setTimeout(() => {
                    // Refresh cart UI
                    if (this.cartDrawer && typeof this.cartDrawer.onCartDrawerUpdate === 'function') {
                        this.cartDrawer.onCartDrawerUpdate();

                        // Make sure our processing runs after the cart updates
                        setTimeout(() => this.processCart(), 500);
                    } else if (this.mainCart && typeof this.mainCart.onCartUpdate === 'function') {
                        this.mainCart.onCartUpdate();

                        // Make sure our processing runs after the cart updates
                        setTimeout(() => this.processCart(), 500);
                    } else if (window.location.pathname.includes('/cart')) {
                        window.location.href = window.location.pathname + '?updated=' + Date.now();
                    } else {
                        // If none of the above work, manually trigger processing
                        setTimeout(() => this.processCart(), 500);
                    }
                }, 300);
            })
            .catch(error => {
                this.log(`Error adding gift option: ${error.message}`, 'error');
                // Reset checkbox
                const checkbox = regularItem.element.querySelector('input.make-it-gift-checkbox');
                if (checkbox) {
                    checkbox.checked = false;
                }
            });
    }

    // Remove a gift option by item key
    removeGiftOption(itemKey, regularItem) {
        this.log(`Removing gift option with key: ${itemKey}`);

        // Immediately update UI to reflect the change
        // Find and remove the visual element
        const giftOption = regularItem.element.querySelector(`.product-gift-option[data-gift-item-key="${itemKey}"]`);
        if (giftOption) {
            giftOption.remove();
        }

        // Check if there are any remaining gift options for this product
        const remainingOptions = regularItem.element.querySelectorAll('.product-gift-option');

        // If no remaining options, uncheck the checkbox immediately
        if (remainingOptions.length === 0) {
            const checkbox = regularItem.element.querySelector('input.make-it-gift-checkbox');
            if (checkbox) {
                checkbox.checked = false;
            }
        }

        // Update cart quantity to 0 for this item
        fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: itemKey,
                quantity: 0
            })
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to remove item: ${response.status}`);
                }
                return response.json();
            })
            .then(cart => {
                this.log(`Gift option removed successfully, new cart count: ${cart.item_count}`);

                // Keep the cart drawer open for drawer UIs
                if (this.cartDrawer) {
                    try {
                        // Try to prevent drawer from closing
                        const cartDrawerElem = document.querySelector('.m-cart-drawer, #cart-drawer, [data-section-type="cart-drawer"]');
                        if (cartDrawerElem) {
                            cartDrawerElem.classList.add('prevent-close');
                            cartDrawerElem.setAttribute('data-updating', 'true');
                        }
                    } catch (e) {
                        this.log('Error preventing drawer close: ' + e.message);
                    }
                }

                // Refresh cart UI after a short delay
                setTimeout(() => {
                    if (this.cartDrawer && typeof this.cartDrawer.onCartDrawerUpdate === 'function') {
                        this.cartDrawer.onCartDrawerUpdate();

                        // Process cart again after refresh
                        setTimeout(() => {
                            this.processCart();

                            // Remove prevention measures
                            if (this.cartDrawer) {
                                try {
                                    const cartDrawerElem = document.querySelector('.m-cart-drawer, #cart-drawer');
                                    if (cartDrawerElem) {
                                        cartDrawerElem.classList.remove('prevent-close');
                                        cartDrawerElem.removeAttribute('data-updating');
                                    }
                                } catch (e) {
                                    this.log('Error removing prevention flags: ' + e.message);
                                }
                            }
                        }, 300);
                    } else if (this.mainCart && typeof this.mainCart.onCartUpdate === 'function') {
                        this.mainCart.onCartUpdate();
                        setTimeout(() => this.processCart(), 300);
                    } else if (window.location.pathname.includes('/cart')) {
                        window.location.href = window.location.pathname + '?updated=' + Date.now();
                    }
                }, 200);
            })
            .catch(error => {
                this.log(`Error removing gift option: ${error.message}`, 'error');

                // If error occurs, revert UI changes
                this.processCart(); // Reprocess to restore the proper state

                // Show error notification
                if (window.MinimogTheme && window.MinimogTheme.Notification) {
                    window.MinimogTheme.Notification.show({
                        target: regularItem.element,
                        type: 'error',
                        message: 'Could not remove item. Please try again.',
                    });
                }
            });
    }

    // Helper method to format money according to Shopify's currency format
    formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            return Shopify.formatMoney(cents);
        }

        // Default formatting if Shopify's method isn't available
        const format = '€{{amount_with_comma_separator}}';
        const value = cents / 100;

        return format.replace('{{amount}}', value.toFixed(2))
            .replace('{{amount_with_comma_separator}}', value.toFixed(2).replace('.', ','));
    }

    // Check if an item has gift option properties
    hasGiftOptionProperties(itemElement) {
        if (!itemElement) return false;

        // Check if there's a Gift Option property
        const properties = itemElement.querySelectorAll('.m-cart-item__property');
        for (const prop of properties) {
            const key = prop.querySelector('[data-cart-item-property-name]')?.textContent.replace(':', '').trim();
            if (key === 'Gift Option') {
                return true;
            }
        }

        return false;
    }

    // Create a gift option display from line item properties
    createGiftOptionFromProperties(itemElement) {
        if (!itemElement) return;

        // Get gift option properties
        const properties = {};
        const propertyElements = itemElement.querySelectorAll('.m-cart-item__property');

        propertyElements.forEach(prop => {
            const key = prop.querySelector('[data-cart-item-property-name]')?.textContent.replace(':', '').trim();
            const value = prop.querySelector('[data-cart-item-property-value]')?.textContent.trim();

            if (key && value) {
                properties[key] = value;
            }
        });

        // Check if we have the required gift option property
        if (!properties['Gift Option']) {
            return;
        }

        this.log(`Creating gift option from properties for item`, 'success', properties);

        // Create container for gift options if it doesn't exist
        let giftOptionsContainer = itemElement.querySelector('.product-gift-options');

        if (!giftOptionsContainer) {
            this.log('Creating new gift options container for property-based gift option');
            giftOptionsContainer = document.createElement('div');
            giftOptionsContainer.className = 'product-gift-options';

            // Insert after the product info
            const productInfo = itemElement.querySelector(this.selectors.cartItemInfo);
            if (productInfo) {
                productInfo.appendChild(giftOptionsContainer);
                this.log('Added gift options container to product info');
            } else {
                this.log('Could not find product info element to attach gift options container');
                return;
            }
        } else {
            // Container exists, check if we've already created this gift option
            if (giftOptionsContainer.querySelector(`[data-gift-option-title="${properties['Gift Option']}"]`)) {
                this.log('Gift option already exists in container, not duplicating');
                return;
            }
        }

        // Create the gift option element
        const giftOptionEl = document.createElement('div');
        giftOptionEl.className = 'product-gift-option';
        giftOptionEl.dataset.giftOptionTitle = properties['Gift Option'];

        // Determine gift type
        const giftType = properties['_gift_option_type'] ||
            (properties['Gift Option'].toLowerCase().includes('box') ? 'box' :
                (properties['Gift Option'].toLowerCase().includes('bag') ? 'bag' : 'gift'));

        const iconClass = giftType === 'box' ? 'gift-box-icon' :
            (giftType === 'bag' ? 'gift-bag-icon' : '');

        // Get image based on type
        let imageSrc = '';
        if (giftType === 'box') {
            imageSrc = 'https://cdn.shopify.com/s/files/1/0928/3200/6485/files/La_Jolla_Box_AI.png?v=1743773369';
        } else if (giftType === 'bag') {
            imageSrc = 'https://cdn.shopify.com/s/files/1/0928/3200/6485/files/LaJollaBagAIOption1_2_2b67e194-ffa1-4f95-be44-28569abbac22.png?v=1743773396';
        }

        // Create the HTML
        giftOptionEl.innerHTML = `
            <div class="gift-option-content">
                ${imageSrc ? `<img src="${imageSrc}" alt="${properties['Gift Option']}" class="gift-option-image ${iconClass}">` : ''}
                <div class="gift-option-info">
                    <span class="gift-option-variant">${properties['Gift Option']}</span>
                    <span class="gift-option-price">${properties['_gift_option_price'] || ''}</span>
                </div>
            </div>
            <div class="gift-option-actions">
                <button class="gift-option-remove-property" data-property-name="Gift Option">Remove</button>
            </div>
        `;

        giftOptionsContainer.appendChild(giftOptionEl);
        this.log(`Added gift option element for "${properties['Gift Option']}" to container`);

        // Add event listener for remove button
        const removeButton = giftOptionEl.querySelector('.gift-option-remove-property');
        if (removeButton) {
            removeButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Get the cart item key
                const itemKey = itemElement.querySelector('[data-item-key]')?.dataset.itemKey;
                if (!itemKey) {
                    this.log('Cannot remove gift option property: item key not found', 'error');
                    return;
                }

                this.removeGiftOptionProperty(itemKey, itemElement);
            });
        }
    }

    // Remove a gift option property from an item
    removeGiftOptionProperty(itemKey, itemElement) {
        this.log(`Removing gift option property from item with key: ${itemKey}`);

        // Show loading state
        const removeButton = itemElement.querySelector('.gift-option-remove-property');
        if (removeButton) {
            removeButton.textContent = 'Removing...';
            removeButton.disabled = true;
        }

        // Get current cart data to find the item
        fetch(`${window.Shopify.routes.root}cart.js`)
            .then(response => response.json())
            .then(cart => {
                // Find the item in the cart
                const item = cart.items.find(item => item.key === itemKey);
                if (!item) {
                    throw new Error('Item not found in cart');
                }

                // Create updated properties without gift option
                const updatedProperties = {};
                if (item.properties) {
                    // Copy all properties except gift option related ones
                    Object.keys(item.properties).forEach(key => {
                        if (!this.giftOptionProperties.includes(key)) {
                            updatedProperties[key] = item.properties[key];
                        }
                    });
                }

                // Update the item properties
                return fetch(`${window.Shopify.routes.root}cart/change.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: itemKey,
                        properties: updatedProperties
                    })
                });
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to update item: ${response.status}`);
                }
                return response.json();
            })
            .then(cart => {
                this.log(`Gift option property removed successfully, refreshing cart`);

                // Remove the visual element
                const giftOption = itemElement.querySelector(`.product-gift-option`);
                if (giftOption) {
                    giftOption.remove();
                }

                // Uncheck the checkbox
                const checkbox = itemElement.querySelector('.make-it-gift-checkbox');
                if (checkbox) {
                    checkbox.checked = false;
                }

                // Update the cart UI
                if (this.cartDrawer && typeof this.cartDrawer.onCartDrawerUpdate === 'function') {
                    this.cartDrawer.onCartDrawerUpdate();
                } else if (this.mainCart && typeof this.mainCart.onCartUpdate === 'function') {
                    this.mainCart.onCartUpdate();
                } else if (window.location.pathname.includes('/cart')) {
                    window.location.href = window.location.pathname + '?updated=' + Date.now();
                }
            })
            .catch(error => {
                this.log(`Error removing gift option property: ${error.message}`, 'error');

                // Reset button state
                if (removeButton) {
                    removeButton.textContent = 'Remove';
                    removeButton.disabled = false;
                }

                // Show error notification
                if (window.MinimogTheme && window.MinimogTheme.Notification) {
                    window.MinimogTheme.Notification.show({
                        target: itemElement,
                        type: 'error',
                        message: 'Could not remove gift option. Please try again.',
                    });
                }
            });
    }

    // Add a gift option as a property to an existing cart item
    addGiftOptionProperty(itemKey, giftOption, regularItem) {
        this.log(`Adding gift option "${giftOption.title}" as property to item with key: ${itemKey}`);

        // Get current cart data to find the item
        fetch(`${window.Shopify.routes.root}cart.js`)
            .then(response => response.json())
            .then(cart => {
                // Find the item in the cart
                const item = cart.items.find(item => item.key === itemKey);
                if (!item) {
                    throw new Error('Item not found in cart');
                }

                // Create updated properties with gift option
                const updatedProperties = item.properties || {};

                // Add gift option properties
                updatedProperties['Gift Option'] = giftOption.title;
                updatedProperties['_gift_option_id'] = giftOption.id;
                updatedProperties['_gift_option_price'] = giftOption.price;
                updatedProperties['_gift_option_type'] = giftOption.type ||
                    (giftOption.title.toLowerCase().includes('box') ? 'box' :
                        (giftOption.title.toLowerCase().includes('bag') ? 'bag' : 'gift'));

                // Update the item properties
                return fetch(`${window.Shopify.routes.root}cart/change.js`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: itemKey,
                        properties: updatedProperties
                    })
                });
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to update item: ${response.status}`);
                }
                return response.json();
            })
            .then(cart => {
                this.log(`Gift option property added successfully, refreshing cart`);

                // Update the cart UI
                if (this.cartDrawer && typeof this.cartDrawer.onCartDrawerUpdate === 'function') {
                    this.cartDrawer.onCartDrawerUpdate();
                } else if (this.mainCart && typeof this.mainCart.onCartUpdate === 'function') {
                    this.mainCart.onCartUpdate();
                } else if (window.location.pathname.includes('/cart')) {
                    window.location.href = window.location.pathname + '?updated=' + Date.now();
                }
            })
            .catch(error => {
                this.log(`Error adding gift option property: ${error.message}`, 'error');

                // Uncheck the checkbox
                const checkbox = regularItem.element.querySelector('.make-it-gift-checkbox');
                if (checkbox) {
                    checkbox.checked = false;
                }

                // Show error notification
                if (window.MinimogTheme && window.MinimogTheme.Notification) {
                    window.MinimogTheme.Notification.show({
                        target: regularItem.element,
                        type: 'error',
                        message: 'Could not add gift option. Please try again.',
                    });
                }
            });
    }

    // Set up listeners for checkout actions
    setupCheckoutListeners() {
        this.log('Setting up checkout listeners');

        // Listen for clicks on checkout buttons
        document.addEventListener('click', (e) => {
            const isCheckoutButton =
                e.target.matches('button[name="checkout"]') ||
                e.target.matches('input[name="checkout"]') ||
                e.target.matches('.checkout-button') ||
                e.target.matches('[href="/checkout"]') ||
                (e.target.closest('a[href="/checkout"]') !== null);

            if (isCheckoutButton) {
                this.log('Checkout button clicked, ensuring gift options are preserved');

                // Give time for our processing to complete
                // This will ensure that visual changes in the cart are consistent with actual cart data
                if (!e.target.hasAttribute('data-gift-processed')) {
                    e.preventDefault();
                    e.stopPropagation();

                    const checkoutButton = e.target.closest('a[href="/checkout"]') || e.target;
                    checkoutButton.setAttribute('data-gift-processed', 'true');

                    // Process any final gift options
                    this.processCart();

                    // Then proceed to checkout after a short delay
                    setTimeout(() => {
                        this.log('Proceeding to checkout');
                        if (checkoutButton.tagName === 'A') {
                            window.location.href = checkoutButton.href;
                        } else {
                            checkoutButton.click();
                        }
                    }, 500);
                }
            }
        }, true);
    }

    // Helper method to ensure a gift checkbox exists and is checked
    ensureGiftCheckboxExists(itemElement) {
        if (!itemElement) return null;

        // Check if already has a checkbox
        let checkbox = itemElement.querySelector('.make-it-gift-checkbox');
        if (checkbox) return checkbox;

        // Find title element to append checkbox after
        const titleElement = itemElement.querySelector('.m-cart-item__title');
        if (!titleElement) return null;

        // Create checkbox container
        const checkboxContainer = document.createElement('div');
        checkboxContainer.className = 'gift-checkbox-container';
        checkboxContainer.style.marginTop = '6px';
        checkboxContainer.style.marginBottom = '6px';

        // Add checkbox HTML
        checkboxContainer.innerHTML = `
            <label style="display: flex; align-items: center; font-size: 0.9em; color: #555;">
                <input type="checkbox" class="make-it-gift-checkbox" style="margin-right: 5px;">
                Make it a gift?
            </label>
        `;

        // Insert after title
        titleElement.after(checkboxContainer);

        // Return the new checkbox
        checkbox = checkboxContainer.querySelector('.make-it-gift-checkbox');

        // Add initialize attribute
        checkbox.setAttribute('data-initialized', 'true');

        // Add event listener
        const regularItem = {
            element: itemElement,
            productId: itemElement.dataset.productId || '',
            handle: itemElement.dataset.productHandle || '',
            title: titleElement.textContent.trim() || ''
        };

        checkbox.addEventListener('change', (e) => this.handleGiftCheckboxChange(e, regularItem));
        this.log(`Added gift checkbox and listener for product ${regularItem.title}`);

        return checkbox;
    }
}

// Initialize on document ready
document.addEventListener('DOMContentLoaded', () => {
    new GiftItemGrouping();
});

// Add a special function to fix the full cart page
window.fixFullCartPage = function () {
    console.log('[GiftItemGrouping] Fixing full cart page');

    if (window.giftItemGroupingInstance) {
        window.giftItemGroupingInstance.processCart();
        console.log('[GiftItemGrouping] Used existing instance to process the cart');
        return 'Processed cart using existing instance';
    }

    return 'No gift item grouping instance available';
};
